import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import PaymentProfileItem from "features/main/HaulPay/modules/Broker/PaymentProfileItem";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { IPaymentProfile } from "models/HaulPay";
import { connectPaymentProfileAPI } from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

interface IProps {
  profilesData?: (IPaymentProfile & { is_existing: boolean })[];
  selectedPaymentProfile?: IPaymentProfile;
  handleNext: VoidFunction;
  setSelectedPaymentProfile: React.Dispatch<React.SetStateAction<IPaymentProfile | undefined>>;
}

export default function ConnectExistingProfile({
  profilesData,
  selectedPaymentProfile,
  handleNext,
  setSelectedPaymentProfile
}: Readonly<IProps>) {
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const [isAlreadyConnectedModalOpen, toggleAlreadyConnectedModalOpen] = useToggle();
  const [companyID, setCompanyID] = useState("");
  const userDetails = useSelector(selectUserDetails);
  const { trackAllEvents } = useAnalytics();

  function goBack() {
    navigate(-1);
  }

  const connectPaymentProfile = async (profile_id: string) => {
    try {
      const { data: connectionResult } = await connectPaymentProfileAPI(
        userDetails.factoring_id,
        profile_id
      );
      setSelectedPaymentProfile(connectionResult.payment_profile);
      setCompanyID(
        connectionResult.payment_profile.company_mc || connectionResult.payment_profile.company_dot
      );
      trackAllEvents("add_carrier_broker_relation_page2");
    } catch (err: any) {
      showErrorMessage(err.message);
    }
  };

  const handleSelectProfile = async (profile: IPaymentProfile) => {
    setSelectedPaymentProfile(profile);
    const connectedProfile = profilesData?.find((profileData) => profileData.id === profile.id);
    if (!isDesktop) {
      await connectPaymentProfile(profile.id);
      if (connectedProfile?.is_existing) {
        toggleAlreadyConnectedModalOpen();
      } else {
        handleNext();
      }
    }
  };

  const onNext = async () => {
    if (isDesktop) {
      const connectedProfile = profilesData?.find(
        (profileData) => profileData.id === selectedPaymentProfile?.id
      );
      await connectPaymentProfile(selectedPaymentProfile?.id ?? "");
      if (connectedProfile?.is_existing) {
        toggleAlreadyConnectedModalOpen();
      } else {
        handleNext();
      }
    }
  };

  return (
    <>
      <form className={styles.connectAddCarrierContainer}>
        <h3 className={styles.title}>Add a Carrier</h3>

        <div className={styles.description}>
          <span>An existing profile matches the email address you’ve entered.</span>
          <div>
            <span>Any questions or concerns please email us at </span>
            <a className="text-info" href="mailto:invitedcarriers@haulpay.com">
              invitedcarriers@haulpay.com
            </a>
          </div>
        </div>

        {profilesData
          ? profilesData.map((profileData) => (
              <PaymentProfileItem
                key={profileData.id}
                isSelected={profileData.id === selectedPaymentProfile?.id}
                data={profileData}
                onSelectProfile={() => handleSelectProfile(profileData)}
              />
            ))
          : null}

        <div className={styles.footer}>
          <Button
            variant="outline"
            rounded
            size="lg"
            onClick={goBack}
            className="desktop:!rounded-base desktop:!w-48">
            {isDesktop ? "Cancel" : "CANCEL"}
          </Button>
          <Button
            rounded
            size="lg"
            onClick={onNext}
            disabled={!selectedPaymentProfile}
            className="desktop:!rounded-base desktop:!w-48">
            {isDesktop ? "Next" : "NEXT"}
          </Button>
        </div>
      </form>
      <ConfirmationModal
        content={`The carrier with ${companyID} is already connected to you and can be found in the Carriers section`}
        isConfirmModalOpen={isAlreadyConnectedModalOpen}
        toggleConfirmModal={toggleAlreadyConnectedModalOpen}
        isHideCancelButton
        title="Already connected!"
        onOK={goBack}
      />
    </>
  );
}
